import { isAdminBlockedFlag } from '../../../Helpers/Chat/adminBlock';
import { formatUserIdToJid, getUserDetails } from '../../../Helpers/Chat/User';
import { getMentionNameFromRoaster } from '../../../Helpers/Utility';

export const EVERYONE_ID = 'all';
const EVERYONE_WORDS = ['all', 'everyone'];
const MENTION_PLACEHOLDER = '@[?]';
const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };

const escapeHtml = text => String(text).replace(/[&<>"]/g, char => HTML_ESCAPES[char]);

/**
 * Label of a mention chip without the '@': the public user name, else the display name.
 *
 * @param {string} userId Mentioned user id, or EVERYONE_ID.
 * @param {object} publicUserNames Public user names by user id.
 * @returns {string} The label.
 */
export const toMentionLabel = (userId, publicUserNames = {}) =>
    userId === EVERYONE_ID
        ? EVERYONE_ID
        : publicUserNames[userId] || getMentionNameFromRoaster(getUserDetails(userId)) || userId;

/**
 * Composer HTML of a mention chip, followed by a space. "Everyone" carries no user id: it is
 * sent as the plain text "@all".
 *
 * @param {string} userId Mentioned user id, or EVERYONE_ID.
 * @param {string} label Chip label without the '@'.
 * @returns {string} The chip HTML.
 */
export const toMentionChipHtml = (userId, label) =>
    userId === EVERYONE_ID
        ? `<span data-mention-all="true" class="mentioned blue" contenteditable="false">@${EVERYONE_ID}</span> `
        : `<span data-mentioned="${escapeHtml(userId)}" class="mentioned blue" contenteditable="false" style="cursor: pointer;">@${escapeHtml(label)}</span> `;

/**
 * Text a mention chip is sent as: the "@[?]" placeholder matched to mentionedUsersIds, or "@all".
 *
 * @param {HTMLElement} chip Mention chip element.
 * @returns {string} The text to send.
 */
export const toMentionPlaceholder = chip => (chip.dataset?.mentionAll ? `@${EVERYONE_ID}` : MENTION_PLACEHOLDER);

/**
 * Name a member is listed and searched by: the contact name, else the display name.
 *
 * @param {object} participant Group participant, with rosterData filled by the composer.
 * @returns {string} The name.
 */
export const getMentionDisplayName = participant =>
    participant?.rosterData?.displayName || participant?.userProfile?.nickName || participant?.userId || '';

/**
 * Whether the typed query still fits "Everyone", which is offered while it does.
 *
 * @param {string} query Text typed after '@'.
 * @returns {boolean} True when "Everyone" matches.
 */
export const isEveryoneQuery = (query = '') => {
    const typed = query.trim().toLowerCase();
    return EVERYONE_WORDS.some(word => word.startsWith(typed));
};

/**
 * Members whose display name or public user name contains the query.
 *
 * @param {object[]} participants Group participants.
 * @param {string} query Text typed after '@'.
 * @param {object} publicUserNames Public user names by user id.
 * @returns {object[]} Matching participants, in the given order.
 */
export const filterMentionMembers = (participants = [], query = '', publicUserNames = {}) => {
    const typed = query.trim().toLowerCase();
    return participants.filter(
        participant =>
            !typed ||
            getMentionDisplayName(participant).toLowerCase().includes(typed) ||
            (publicUserNames[participant.userId] || '').toLowerCase().includes(typed),
    );
};

/**
 * Members the user can mention (FR103): active accounts that have not blocked them, except
 * themselves, sorted by name A to Z.
 *
 * @param {object} params
 * @param {object[]} params.participants Group participants already matched to the query.
 * @param {string[]} params.blockedMeJids JIDs of users who blocked the local user.
 * @param {string} params.localUserId Local user id.
 * @returns {object[]} Mentionable participants.
 */
export const toMentionMembers = ({ participants = [], blockedMeJids = [], localUserId }) =>
    participants
        .filter(
            participant =>
                participant.userId !== localUserId &&
                !isAdminBlockedFlag(participant.isAdminBlocked) &&
                participant.rosterData?.isDeletedUser !== true &&
                !blockedMeJids.includes(formatUserIdToJid(participant.userId)),
        )
        .sort((a, b) =>
            getMentionDisplayName(a).localeCompare(getMentionDisplayName(b), undefined, { sensitivity: 'base' }),
        );
